import React, {useEffect, useState} from 'react';
import {Dialog} from '@headlessui/react';
import {MultiValue} from 'react-select';
import {ClientType} from "../models/enums/ClientType";
import {ClientStatus} from "../models/enums/ClientStatus";
import {ContactOption} from "../models/enums/ContactOption";
import CreatableSelect from "react-select/creatable";
import { useAuth } from '../context/AuthContext';
import {createNewClient} from "../services/client-service";
import ClientDTO from "../models/ClientDTO";
import Client from "../models/Client";

interface AddClientModalProps {
    isOpen: boolean;
    onClose: () => void;
    onError: (errorMessage: string) => void;
    onAddClient: (newClient: Client) => void;
}

const initialTagOptions = [
    { label: 'VIP', value: 'VIP' },
    { label: 'High Priority', value: 'High Priority' },
    { label: 'Referral', value: 'Referral' },
];

const AddClientModal: React.FC<AddClientModalProps> = ({ isOpen, onClose, onError, onAddClient }) => {
    type TagOption = { label: string; value: string };
    const maxNotes = 1000;

    const contactOptions = Object.values(ContactOption).map((contact) => ({
        value: contact,
        label: contact,
    }));

    const statusOptions = Object.values(ClientStatus).map((status) => ({
        value: status,
        label: status
    }));

    const initialForm = {
        clientType: ClientType.INDIVIDUAL,
        fullName: '',
        businessName: '',
        contactPhone: '',
        contactEmail: '',
        status: statusOptions[0].value,
        tagOptions: [] as MultiValue<TagOption>,
        tags: [] as string[],
        notes: '',
        preferredContact: contactOptions[0].value,
        userId: null as number | null | undefined
    };

    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [tagOptions, setTagOptions] = useState<TagOption[]>(initialTagOptions);
    const [loading, setLoading] = useState(false);
    const { user } = useAuth();

    useEffect(() => {
        if (!isOpen) {
            setForm(initialForm);
            setErrors({});
        }
    }, [isOpen]);

    const validate = (tags: string[]) => {
        const errs: Record<string, string> = {};
        if (!form.clientType) errs.clientType = 'Select client type';
        if (form.clientType === ClientType.INDIVIDUAL && !form.fullName.trim()) errs.fullName = 'Full name is required';
        if (form.clientType === ClientType.INDIVIDUAL && form.fullName.length > 100) errs.fullName = 'Full name must be 100 characters or fewer';
        if (form.clientType === ClientType.BUSINESS && !form.businessName.trim()) errs.businessName = 'Business name is required';
        if (form.clientType === ClientType.BUSINESS && form.businessName.length > 100) errs.businessName = 'Business name must be 100 characters or fewer';
        if (!form.status) errs.status = 'Status is required';
        if (form.contactEmail && !/.+@.+\..+/.test(form.contactEmail)) errs.contactEmail = 'Invalid email';
        if (form.contactEmail && form.contactEmail.length > 100) errs.contactEmail = 'Email must be 100 characters or fewer';
        if (form.contactPhone && !/^\+?[0-9]{7,15}$/.test(form.contactPhone)) errs.contactPhone = 'Invalid phone number (do not use dashes)';

        const totalTagChars = tags.reduce((sum, t) => sum + t.length, 0);
        if (totalTagChars > 255) {
            errs.tags = `Total tags must be 255 characters or fewer (you have ${totalTagChars})`;
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        setLoading(true);

        let tags: string[] = [];
        form.tagOptions.forEach(tag => {
            tags.push(tag.value);
        });

        if (!validate(tags)) {
            setLoading(false)
            return;
        }

        let clientDTO: ClientDTO = {
            fullName: form.fullName,
            businessName: form.businessName,
            contactPhone: form.contactPhone,
            contactEmail: form.contactEmail,
            status: form.status,
            clientType: form.clientType,
            notes: form.notes,
            tags: tags,
            preferredContact: form.preferredContact,
            userId: user ? user.id : null
        }

        try {
            let response = await createNewClient(clientDTO);
            if (response.status === 201 && response.data) {
                onAddClient(response.data);
            } else if (response.status === 403) {
                onError('Client limit has been reached under your plan. You can upgrade to premium to create more clients.');
            } else {
                console.error('Unable to create new client');
                onError('Unable to create new client at this time. Please try again later');
            }
        } catch (e) {
            console.error('Unable to create new client');
            onError('Unable to create new client at this time. Please try again later');
        } finally {
            setLoading(false);
            onClose();
        }
    };

    return (
        <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Blur + dark overlay */}
            <div
                className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm"
                aria-hidden="true"
                onClick={onClose}
            />

            <div className="relative bg-white w-full max-w-2xl p-6 rounded-lg shadow-lg overflow-auto max-h-screen">
                <Dialog.Title className="text-xl font-semibold mb-4">Add New Client</Dialog.Title>
                <div className="space-y-4">
                    {/* Client Type */}
                    <fieldset className="space-x-4">
                        <legend className="font-medium">Client Type *</legend>
                        <label>
                            <input
                                type="radio"
                                name="clientType"
                                value="individual"
                                checked={form.clientType === ClientType.INDIVIDUAL}
                                onChange={() => setForm({ ...form, clientType: ClientType.INDIVIDUAL, businessName: ''})}
                            />{' '}Individual
                        </label>
                        <label>
                            <input
                                type="radio"
                                name="clientType"
                                value="business"
                                checked={form.clientType === ClientType.BUSINESS}
                                onChange={() => setForm({ ...form, clientType: ClientType.BUSINESS, fullName: '' })}
                            />{' '}Business
                        </label>
                        {errors.clientType && <p className="text-red-600 text-sm">{errors.clientType}</p>}
                    </fieldset>

                    {/* Name */}
                    {form.clientType === ClientType.INDIVIDUAL ? (
                        <div>
                            <label htmlFor="fullName" className="block">Full Name *</label>
                            <input
                                id="fullName"
                                type="text"
                                value={form.fullName}
                                onChange={e => setForm({ ...form, fullName: e.target.value })}
                                className="w-full border rounded p-2"
                            />
                            {errors.fullName && <p
                                className="text-red-600 text-sm"
                                data-testid="full-name-error"
                            >
                                {errors.fullName}
                            </p>}
                        </div>
                    ) : (
                        <>
                            <div>
                                <label htmlFor="businessName" className="block">Business Name *</label>
                                <input
                                    id="businessName"
                                    type="text"
                                    value={form.businessName}
                                    onChange={e => setForm({ ...form, businessName: e.target.value })}
                                    className="w-full border rounded p-2"
                                />
                                {errors.businessName &&
                                    <p
                                        className="text-red-600 text-sm"
                                        data-testid="business-name-error"
                                    >
                                        {errors.businessName}
                                    </p>}
                            </div>
                        </>
                    )}
                    {/* Contact Details */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="email" className="block">Email</label>
                            <input
                                id="email"
                                type="email"
                                value={form.contactEmail}
                                onChange={e => setForm({ ...form, contactEmail: e.target.value })}
                                className="w-full border rounded p-2"
                            />
                            {errors.contactEmail &&
                                <p className="text-red-600 text-sm" data-testid="email-error">{errors.contactEmail}</p>}
                        </div>
                        <div>
                            <label htmlFor="phone" className="block">Phone</label>
                            <input
                                id="phone"
                                type="tel"
                                value={form.contactPhone}
                                onChange={e => setForm({ ...form, contactPhone: e.target.value })}
                                className="w-full border rounded p-2"
                            />
                            {errors.contactPhone && <p className="text-red-600 text-sm">{errors.contactPhone}</p>}
                        </div>
                    </div>

                    {/* Status */}
                    <div>
                        <label htmlFor="client-status" className="block">Status *</label>
                        <select
                            id="client-status"
                            className="w-full border px-3 py-2 rounded"
                            value={form.status}
                            onChange={e => setForm({...form, status: e.target.value as ClientStatus})}
                        >
                            {statusOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                        {errors.status && <p className="text-red-600 text-sm">{errors.status}</p>}
                    </div>

                    {/* Tags*/}
                    <div data-testid="tags-container">
                        <label className="block">Tags</label>
                        <CreatableSelect<TagOption, true>
                            options={tagOptions}
                            isMulti
                            value={form.tagOptions}
                            onChange={vals => setForm({ ...form, tagOptions: vals })}
                            onCreateOption={inputValue => {
                                const newOption = { label: inputValue, value: inputValue };
                                setTagOptions(prev => [...prev, newOption]);
                                setForm(prev => ({ ...prev, tagOptions: [...prev.tagOptions, newOption] }));
                            }}
                            placeholder="Select or type to create tag..."
                        />
                        {errors.tags && <p className="text-red-600 text-sm" data-testid="tags-error">{errors.tags}</p>}
                    </div>

                    {/* Notes */}
                    <div>
                        <label htmlFor="notes" className="block">Notes</label>
                        <textarea
                            id="notes"
                            rows={3}
                            maxLength={maxNotes}
                            value={form.notes}
                            onChange={e => setForm({ ...form, notes: e.target.value })}
                            className="w-full border rounded p-2"
                        />
                        <p className="text-gray-500 text-sm mt-1">
                            {form.notes.length}/{maxNotes} characters
                        </p>
                    </div>

                    {/* Contact type */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="preferred-contact" className="block">Preferred Contact Method</label>
                            <select
                                id="preferred-contact"
                                className="w-full border px-3 py-2 rounded"
                                value={form.preferredContact}
                                onChange={e => setForm({...form, preferredContact: e.target.value as ContactOption})}
                            >
                                {contactOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>

                    </div>

                    {/* Actions */}
                    <div className="flex justify-end space-x-3 mt-6">
                        <button onClick={onClose} className="px-4 py-2 rounded border">Cancel</button>
                        <button
                            onClick={handleSubmit}
                            className="px-4 py-2 rounded bg-crumbblue text-white"
                            disabled={loading}
                        >
                            {loading ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default AddClientModal;